import { useMemo } from 'react';
import PropTypes from 'prop-types';
import { FunctionField, RecordContextProvider, ReferenceField, TextField, useGetList, useGetMany, useRecordContext, useTranslate } from 'react-admin';
import { Box, Link, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import { DetailsSampleList } from '@/components/global/fields/DetailsSampleList.jsx';
import { EmptyField } from '@/components/accessKeys/fields/EmptyField.jsx';
import CustomChipField from '@/components/global/fields/CustomChipField.jsx';
import { BrandLogoField } from '@/components/brand/fields/BrandLogoField.jsx';
import { useDrawer } from '@/composables/useDrawer';
import { Palette } from '@/themes/themeOverrides/Palette';
import { brandBasicDetailsStyles } from '@/themes/commonStyles/brandBasicDetails.js';

const StyledBasicDetails = styled(Box)(brandBasicDetailsStyles);

const RetailChainDetailsLink = () => {
    const retailChain = useRecordContext();
    const { openDrawer } = useDrawer();

    if (!retailChain?.id || !retailChain?.name) {
        return <EmptyField />;
    }

    return (
        <Typography variant='body2'>
            <Link
                component='button'
                type='button'
                underline='hover'
                color='inherit'
                onClick={event => {
                    event.stopPropagation();
                    openDrawer(retailChain.id, 'retail-chain-show');
                }}
                sx={{ cursor: 'pointer', border: 'none', background: 'none', padding: 0, font: 'inherit', verticalAlign: 'baseline' }}
            >
                {retailChain.name}
            </Link>
        </Typography>
    );
};

RetailChainDetailsLink.propTypes = {};

const objectRecords = value => (Array.isArray(value) ? value.filter(item => item && typeof item === 'object' && item.id) : []);

const PendingValue = () => (
    <Typography variant='body2' component='span'>
        —
    </Typography>
);

PendingValue.propTypes = {};

const RelatedChips = ({ records, drawerType, labelOf }) => {
    const { openDrawer } = useDrawer();

    if (!records?.length) {
        return <PendingValue />;
    }

    return (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
            {records.map(item => (
                <RecordContextProvider key={item.id} value={{ ...item, name: labelOf(item) }}>
                    <CustomChipField shades color={Palette.gray.gray100} onClick={item.id ? () => openDrawer(item.id, drawerType) : undefined} />
                </RecordContextProvider>
            ))}
        </Box>
    );
};

RelatedChips.propTypes = {
    records: PropTypes.array,
    drawerType: PropTypes.string.isRequired,
    labelOf: PropTypes.func.isRequired,
};

const useBrandContractors = () => {
    const record = useRecordContext();
    const embeddedContractors = objectRecords(record?.contractors);
    const contractorIds = useMemo(() => {
        const ids = record?.contractorIds;
        return Array.isArray(ids) ? ids.filter(Boolean) : [];
    }, [record?.contractorIds]);
    const hasEmbeddedContractors = Array.isArray(record?.contractors);
    const { data: contractorsById = [], isPending: isIdsPending } = useGetMany(
        'contractor',
        { ids: contractorIds },
        { enabled: !hasEmbeddedContractors && contractorIds.length > 0 },
    );
    const { data: contractorsByBrand = [], isPending: isListPending } = useGetList(
        'contractor',
        {
            pagination: { page: 1, perPage: 200 },
            sort: { field: 'name', order: 'ASC' },
            filter: { brandId: record?.id },
        },
        { enabled: Boolean(record?.id) && !hasEmbeddedContractors && contractorIds.length === 0 },
    );

    let contractors = contractorsByBrand;
    if (hasEmbeddedContractors) {
        contractors = embeddedContractors;
    } else if (contractorIds.length > 0) {
        contractors = contractorsById;
    }
    const isPending =
        (!hasEmbeddedContractors && contractorIds.length > 0 && isIdsPending) || (!hasEmbeddedContractors && contractorIds.length === 0 && isListPending);

    return { contractors: contractors || [], isPending };
};

export const BrandBasicDetails = () => {
    const translate = useTranslate();

    return (
        <StyledBasicDetails>
            <DetailsSampleList
                leading={
                    <Box className='basic-details__logo'>
                        <BrandLogoField column />
                    </Box>
                }
            >
                <TextField source='name' label={translate('resources.brand.fields.name')} emptyText='—' />
                <FunctionField source='retailChainId' label={translate('resources.brand.fields.retailChain')} render={() => <BrandRetailChainDetails />} />
                <TextField source='description' label={translate('resources.brand.fields.description')} emptyText='—' />
                <FunctionField source='contractors' label={translate('resources.brand.fields.contractors')} render={() => <BrandContractorsDetails />} />
                <FunctionField
                    source='collectionPoints'
                    label={translate('resources.brand.fields.collectionPoints')}
                    render={() => <BrandCollectionPointsDetails />}
                />
            </DetailsSampleList>
        </StyledBasicDetails>
    );
};

BrandBasicDetails.propTypes = {};

const BrandRetailChainDetails = () => {
    const record = useRecordContext();

    if (!record?.retailChainId) {
        return <EmptyField />;
    }

    return (
        <ReferenceField source='retailChainId' reference='retail-chain' link={false}>
            <RetailChainDetailsLink />
        </ReferenceField>
    );
};

BrandRetailChainDetails.propTypes = {};

export const BrandContractorsDetails = () => {
    const { contractors, isPending } = useBrandContractors();

    if (isPending) {
        return <PendingValue />;
    }

    return <RelatedChips records={contractors} drawerType='contractor-show' labelOf={contractor => contractor.name} />;
};

export const BrandCollectionPointsDetails = () => {
    const record = useRecordContext();
    const embeddedCollectionPoints = objectRecords(record?.collectionPoints);
    const hasEmbeddedCollectionPoints = Array.isArray(record?.collectionPoints);
    const { contractors, isPending: areContractorsPending } = useBrandContractors();
    const contractorIds = useMemo(() => contractors.map(contractor => contractor.id).filter(Boolean), [contractors]);
    const { data: collectionPoints = [], isPending } = useGetList(
        'collection-point',
        {
            pagination: { page: 1, perPage: 200 },
            sort: { field: 'customId', order: 'ASC' },
            filter: { contractorId_in: contractorIds },
        },
        { enabled: !hasEmbeddedCollectionPoints && !areContractorsPending && contractorIds.length > 0 },
    );

    if (!hasEmbeddedCollectionPoints && (areContractorsPending || (contractorIds.length > 0 && isPending))) {
        return <PendingValue />;
    }

    const points = hasEmbeddedCollectionPoints ? embeddedCollectionPoints : collectionPoints;

    return <RelatedChips records={points} drawerType='collection-point-show' labelOf={point => point.customId || point.shopName || point.name} />;
};

BrandContractorsDetails.propTypes = {};

BrandCollectionPointsDetails.propTypes = {};
